import type { Forms } from './plural'

export const en = {
  common: {
    loading: 'Loading…',
    failed: 'Failed',
    retry: 'Retry',
    reload: 'Reload',
    done: 'Done',
    next: 'Next',
    back: 'Back',
    finish: 'Finish',
    cancel: 'Cancel',
    clear: 'Clear',
    of: 'of',
    optional: 'optional',
    nothingFound: 'Nothing found',
    unknown: 'unknown',
    notFoundTitle: 'Page not found',
    notFoundHint: 'That link leads nowhere.',
    error: (status: number) =>
      status === 404
        ? 'Nothing there'
        : status === 401 || status === 403
          ? 'No access'
          : `Didn't go through, error ${status}`,
  },

  units: {
    items: ['item', 'items'] as Forms,
    updates: ['update', 'updates'] as Forms,
    actions: ['action', 'actions'] as Forms,
    products: ['product', 'products'] as Forms,
    days: ['day', 'days'] as Forms,
    weeks: ['week', 'weeks'] as Forms,
    components: ['component', 'components'] as Forms,
    expires: ['expires', 'expire'] as Forms,
    photos: ['photo', 'photos'] as Forms,
    uses: ['use', 'uses'] as Forms,
    steps: ['step', 'steps'] as Forms,
  },

  nav: {
    today: 'Today',
    week: 'Week',
    products: 'Products',
    spf: 'SPF',
    more: 'More',
  },

  today: {
    title: 'Today',
    stepsDone: 'steps done',
    emptyTitle: 'Nothing for today',
    emptyHint: 'Steps appear once the AI sends a routine.',
    skipReasons: [
      { value: 'forgot', label: 'Forgot' },
      { value: 'skin irritated', label: 'Skin irritated' },
      { value: 'ran out', label: 'Ran out' },
      { value: 'no time', label: 'No time' },
      { value: 'chose not to', label: 'Chose not to' },
    ],
    ownReason: 'chose not to',
    ownReasonLabel: 'Why not',
    ownReasonTags: ['stickiness', 'laziness', 'in a rush', 'smell', 'skin is fine as is'],
    skipTitle: 'Skip step',
    skipShort: 'Skip',
    skipReasonLabel: 'Reason',
    skipSave: 'Save skip',
    clearMark: 'Reset',
    stung: 'It stung today',
    stungClear: "It didn't sting",
    stungMark: 'stung',
    openProduct: 'Open product card',
    ownReasonPlaceholder: 'in your own words — optional',
    more: 'More',
  },

  week: {
    title: 'Week',
    today: 'today',
    noData: 'no data',
    full: 'full',
    partial: 'partial',
  },

  spf: {
    title: 'SPF',
    applied: 'Applied',
    notAppliedYet: 'Not applied yet',
    notMarkedToday: 'Not marked today',
    noUpdates: 'no updates',
    dayGoesOn: 'day goes on',
    apply: 'Mark applied',
    unapply: 'Reset',
    reapply: 'Mark reapplication',
    marked: 'SPF marked',
    monthLabel: 'this month',
    notMarked: 'Not marked',
    todayIs: (date: string) => `today, ${date}`,
    withApplication: 'with application',
    exposureTitle: 'Where are you spending the day?',
    exposureIndoors: 'Indoors',
    exposureWindow: 'By a window',
    exposureOutdoors: 'Outdoors',
    exposureHint: {
      indoors: 'One layer in the morning is plenty: two fingers for face and neck, no reapplying.',
      window:
        'A closed window still lets long UVA through. One layer in the morning, a second after lunch, two fingers each.',
      outdoors:
        'Outside, a layer lasts about two hours: reapply every two hours and after water, two fingers each time, and count each one here.',
    } as Record<string, string>,
    exposureShort: {
      indoors: 'indoors',
      window: 'by a window',
      outdoors: 'outdoors',
    } as Record<string, string>,
    byWindow: 'of those by a window',
    reappliedOn: (days: string) => `reapplied on ${days}`,
    legendNone: 'not marked',
    legendApplied: 'applied',
    legendReapplied: 'with reapplication',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
  },

  products: {
    title: 'Products',
    searchPlaceholder: 'Name, brand or ingredient',
    clearSearch: 'Clear search',
    inRoutine: 'in routine',
    emptyNoRoutine: 'Products show up once the AI sends a routine.',
    emptyTryAnother: 'Try another word or drop the filter.',
    all: 'All',
    routine: 'In routine',
    shelf: 'On the shelf',
    noSlot: 'Not in routine',
    order: 'Order',
    orderLabel: 'List order',
    orderFlat: 'one list',
    orderHeaded: 'with headings',
    orderName: 'By name',
    orderRemaining: 'Running out first',
    orderSlot: 'By slot',
    orderStatus: 'By status',
  },

  product: {
    purpose: 'purpose',
    inRoutine: 'in routine',
    notInRoutine: 'Not in the current routine — sitting on the shelf.',
    noInci: 'Not filled in yet. The actives breakdown shows up with it.',
    stockUnknown: 'Unknown. Counting starts with your first stock mark.',
    noNotes: 'Empty.',
    stock: 'stock',
    expires: (date: string) => `expires ${date}`,
    expiryUncertain: 'expiry date uncertain',
    usedSince: (uses: string, date: string) => `${uses} since ${date}.`,
    usedSinceLeft: (uses: string, date: string, left: number) =>
      `${uses} since ${date}, about ${left} left.`,
    lastsAbout: (weeks: string) => `At this pace it should last about ${weeks} more.`,
    actives: 'actives',
    fullInci: 'full INCI',
    collapse: 'Collapse',
    expand: 'Expand',
    composition: 'composition',
    preservatives: 'preservatives',
    afterPreservative: 'after the preservative',
    traceHint: 'Below the line — trace amounts.',
    expectedEffect: 'expected effect',
    evaluateAfter: (weeks: string) => `judge after ${weeks} of use`,
    notes: 'notes',
  },

  flags: {
    kinds: {
      'severe-reaction': 'Severe reaction',
      worsening: 'Getting worse',
      'spf-gap': 'SPF missing',
    } as Record<string, string>,
  },

  conflicts: {
    myth: 'myth',
  },

  checkin: {
    title: 'Check-in',
    doneTitle: 'Check-in complete',
    doneSubtitle: 'Your answers go into the export just as you wrote them',
    saved: 'Check-in saved',
    yes: 'yes',
    no: 'no',
    textPlaceholder: 'In your own words',
    eyebrow: (type: string, index: number, total: number, multi: boolean) =>
      `${type} check-in, ${index} of ${total}${multi ? ', multiple allowed' : ''}`,
  },

  reaction: {
    title: 'Reaction',
    steps: ['What happened?', 'Where?', 'How severe?', 'Suspected cause?', 'What did you do?'],
    eyebrow: (step: number, total: number) => `reaction, ${step} of ${total}`,
    dontKnow: "don't know",
    actionPlaceholder: 'Rinsed off, applied cream, stopped the product…',
    record: 'Record',
    recorded: 'Reaction recorded',
    zones: ['forehead', 'nose', 'cheeks', 'chin', 'neck', 'everywhere'],
  },

  history: {
    title: 'History',
    emptyTitle: 'Empty for now',
    emptyHint: 'Your first mark on "Today" starts this log.',
    reaction: 'reaction',
    checkin: 'check-in',
    morning: 'morning',
    evening: 'evening',
    whole: 'whole',
    spfApplied: 'SPF applied',
    spfNotMarked: 'SPF not marked',
    routineEdited: 'routine edited',
    nothingMarked: 'nothing marked',
    skipped: (product: string, reason: string) => `${product} skipped — ${reason}`,
  },

  historyDay: {
    alsoThatDay: 'also that day',
    reactionRecorded: 'Reaction recorded',
    suspected: (product: string) => `, suspected ${product}`,
    weeklyCheckin: 'Weekly check-in',
    monthlyCheckin: 'Monthly check-in',
    routine: 'Routine',
    perDay: 'per day',
    exposure: (place: string) => `, ${place}`,
  },

  export: {
    title: 'Export',
    adherence: 'Routine adherence',
    checkinAnswers: 'Check-in answers',
    reactions: 'Reactions',
    productsAndUse: 'Products and use',
    expiry: 'Expiry dates',
    expiringSuffix: 'in the next three months',
    conflicts: 'Conflicts found',
    consultant: 'AI consultant',
    consultantConnected: 'It pulls these facts itself, so there is nothing to send.',
    consultantMissing: 'Not connected. You can get the link in "Connection".',
    files: 'files',
    backupTitle: 'Backup',
    backupHint: 'Everything the app stores, so you can put it back later.',
    saveBackup: 'Save backup',
    restoreAction: 'Restore from file',
    consultantFile: 'Export for the consultant',
    consultantFileHint:
      'A file for any AI assistant: attach it to the chat with the prompt. For when there is no connector.',
    saveFile: 'Save file',
    saved: 'The download opened in your browser',
    failed: 'Export failed',
    restoreTitle: 'Restore from file',
    restoreReplaces: 'This wipes everything in the app right now and puts the file in its place.',
    restoreConfirm: 'Replace data',
    restoreCancel: 'Cancel',
    restoreBad: 'This file is not a nitea backup',
    restoreDone: 'Data restored from the file',
    restoreFailed: 'Restore failed',
    fileProducts: 'Products',
    fileMarks: 'Routine marks',
    fileCheckins: 'Check-ins',
    filePeriod: 'Period',
    fileVersion: 'Schema version',
    footnote: 'No photos, no keys. Marks cover this period only.',
  },

  more: {
    title: 'More',
    routine: 'routine',
    records: 'records',
    settings: 'settings',
    attentionHint: 'What is expiring, what has run out, what the AI flagged',
    notesHint: 'What the AI wrote down and the myths it debunked',
    checkinDone: 'Done this week',
    checkinStarted: 'Started',
    reactionHint: 'Record an episode: what, where, how severe and what you did',
    historyHint: 'Log by day',
    exportHint: 'Facts for the period',
    settingsHint: 'Reminders, weekly check-in, timezone',
    connectionHint: 'Link for the AI consultant, permissions and recent events',
    photos: 'Photos',
    photosHint: 'Frames by angle',
  },

  photos: {
    title: 'Photos',
    angleLabel: 'angle',
    add: 'Add a photo',
    earlier: 'earlier',
    uploading: 'Uploading…',
    delete: 'Delete photo',
    deleteConfirm: 'Delete for good?',
    closeViewer: 'Close photo',
    closeCompare: 'Close comparison',
    compareAction: 'Compare',
    compareLeft: 'left of the handle',
    compareRight: 'right of the handle',
    compareLeftTitle: 'Left photo',
    compareRightTitle: 'Right photo',
    emptyTitle: 'No photos yet',
    emptyHint: 'Take the first one — the next one can be lined up against it.',
    previous: 'previous photo',
    latest: 'latest photo',
    sourceTitle: 'Add a photo',
    sourceHint: 'The camera lines your new photo up with the last one.',
    camera: 'Take a photo',
    cameraFallback: 'Choose a file instead',
    cameraHint: 'This device has no camera the app can open.',
    cameraFailed: "Camera didn't open",
    gallery: 'Choose a file',
    pose: {
      front: 'Face the camera, eyes on the dashed line',
      left: 'Turn until your left cheek fills the oval',
      right: 'Turn until your right cheek fills the oval',
      detail: 'Bring the spot into the middle of the frame',
    },
    compare: 'Show the previous photo',
    comparing: 'lined up with',
    shutter: 'Take a photo',
    flip: 'Switch camera',
    closeCamera: 'Close camera',
    keepTitle: 'Keep this frame?',
    keepHint: 'previous:',
    retake: 'Retake',
    save: 'Save photo',
  },

  time: {
    digits: 'Digits',
    wheels: 'Wheels',
    badFormat: "Doesn't look like a time",
    later: 'Later',
    earlier: 'Earlier',
  },

  settings: {
    title: 'Settings',
    language: 'language',
    languageNote: 'Switching reloads the app.',
    timezone: 'timezone',
    timezoneTitle: 'Timezone',
    timezoneSearch: 'City or offset',
    timezoneEmpty: 'Try another city or offset — for example "+4" or "Dubai".',
    timezoneFromSystem: 'from system',
    regions: {
      Europe: 'Europe',
      Asia: 'Asia',
      America: 'America',
      Australia: 'Australia',
      Africa: 'Africa',
      Atlantic: 'Atlantic',
      Indian: 'Indian Ocean',
      Antarctica: 'Antarctica',
      Arctic: 'Arctic',
      Pacific: 'Pacific',
      UTC: 'UTC',
    } as Record<string, string>,
    timezoneHint: 'Your day and your stats are counted in this zone.',
    timezoneSystem: 'system',
    timezoneManual: 'changed manually',
    weeklyCheckin: 'weekly check-in',
    day: 'day',
    time: 'time',
    monthlyDay: 'monthly, day',
    monthlyDayField: 'day of month',
    monthlyDayNote: (day: string) => `the ${day} of every month`,
    monthlyDayEdge: '28th is the cap: not every month goes further',
    morningReminder: 'morning reminder',
    morningReminderLabel: 'Remind about the morning routine',
    reminderNote: 'Off by default. The bot waits for "ok", and nudges again an hour later.',
    eveningReminder: 'evening reminder',
    eveningReminderLabel: 'Remind about the evening routine',
    aiReview: 'review with the AI',
    aiReviewLabel: 'Remind me to show my routine to the AI',
    aiReviewNote: "Comes only when the AI hasn't read your record in a month.",
    spfReminder: 'SPF',
    spfReminderNote:
      'While your routine has acids or a retinoid, the bot reminds you about SPF two hours after your morning time. Not a setting.',
    profile: 'skin profile',
    profileEmpty: 'Nothing here yet. An AI client fills this in from what you tell it.',
    profileUpdated: (date: string) => `Recorded ${date}`,
    profileSkinType: 'Skin type',
    profileConditions: 'Conditions',
    profileConcerns: 'Working on',
    profileIntolerances: 'Reacts to',
    profileMedications: 'Medication',
    aiPhotos: 'photos for the AI client',
    aiPhotosLabel: 'Let a connection key see your photos',
    aiPhotosNote:
      'Closed by default. While it is open, an AI client can list your photos and open each one through a signed link, and no link outlives the window. Uploading and deleting stay in the app.',
    aiPhotosOff: 'Closed',
    aiPhotosHour: '1 hour',
    aiPhotosDay: '24 hours',
    aiPhotosAlways: 'Always',
    aiPhotosUntil: (moment: string) => `open until ${moment}`,
    aiPhotosAlwaysNote: 'Open until you close it.',
    weekdayField: 'check-in weekday',
    weekdays: [
      { value: 1, label: 'Mon', weekend: false },
      { value: 2, label: 'Tue', weekend: false },
      { value: 3, label: 'Wed', weekend: false },
      { value: 4, label: 'Thu', weekend: false },
      { value: 5, label: 'Fri', weekend: false },
      { value: 6, label: 'Sat', weekend: true },
      { value: 0, label: 'Sun', weekend: true },
    ],
    weekdayEvery: [
      'every Sunday',
      'every Monday',
      'every Tuesday',
      'every Wednesday',
      'every Thursday',
      'every Friday',
      'every Saturday',
    ] as string[],
    hour: 'hour',
    minute: 'minute',
    less: 'Less',
    more: 'More',
  },

  connection: {
    title: 'Connection',

    yourLinkHint:
      'Paste it into Claude or ChatGPT once, as a connector. It works until you replace it.',
    copyLink: 'Copy link',
    showKey: 'Show the key',
    hideKey: 'Hide the key',
    replace: 'Replace',
    linkIsKey: 'The link is the key. Pass it on and you hand over write access to your log.',
    hiddenOnLeave: 'Hidden again when you leave the screen.',
    noLinkYet: 'No key yet. Get one and paste it into your AI client as a connector.',
    clientName: 'AI consultant',
    copy: 'Copy',
    copied: 'Link copied',
    noKey: 'No key issued yet',
    issue: 'Get link',
    revoke: 'Revoke',
    revoked: 'Key revoked',
    revokeNote: 'Your data stays. Only access goes away.',
    permissions: 'what the AI can do',
    keys: 'key',
    events: 'recent events',
    inUse: 'in use',
    notUsedYet: 'not used yet',
    since: 'issued',
    statusNotConfigured: 'MCP not configured',
    statusConnected: 'Connected',
    statusWaiting: 'Waiting for the AI',
    statusDisconnected: 'Not connected',
    keyUsed: 'key already used',
    linkIssued: 'link issued',
    canWrite: 'Send the routine',
    canRead: 'Read facts for the period',
    canSeeReactions: 'See reactions and flags',
    photos: 'Photos',
    write: 'write',
    read: 'read',
    forbidden: 'forbidden',
  },

  onboarding: {
    title: 'Nothing to do yet',
    hint: 'Steps appear when the AI sends a routine. Three things get you there.',
    stepLink: 'Copy your link',
    stepLinkHint: "It's the key to your log. Don't pass it on.",
    copyLink: 'Copy link',
    stepConnector: 'Add it as a connector',
    stepMessage: 'Send it the first message',
    stepMessageHint:
      'A ready prompt: it tells the assistant what to ask you and what to write down. Paste it, then answer the questions.',
    copyMessage: 'Copy',
    read: 'Read it',
    leaves: 'This card goes away by itself when the first step arrives.',
    messageTitle: 'First message',
    messageSubtitle: 'Paste it into the chat where the connector is added.',
    messageCopied: 'First message copied',
    close: 'Close',
    keptTitle: 'First message',
    keptHint: 'Kept next to the link, for a second client or a fresh chat.',
    firstMessage: `Act as my skincare consultant. I keep my routine in an app called nitea and have just connected it to you: you can read it and write to it.

Before advising anything:
1. Call get_profile, get_routine, list_products and get_settings. Do not ask me for what you can already read.
2. Ask which language the record should be kept in, and say what get_settings reports. If it should change, call set_settings. Everything you write after that — products, steps, reasons, notes — goes in that language; a product name stays as printed on the package.
3. Interview me, a few questions at a time, in this order: skin type and how it behaves through the day; diagnosed conditions and any medication, including oral; what bothers me most and for how long; what I have reacted badly to; what I already own, with names; my city, climate and how much sun I get; how much time the routine can take.
4. Write my answers into the app as we go, update_profile for the facts and add_product for what I own, always with a reason.
5. Only then build the routine with add_step: morning, evening, as needed. For every step say what it is for, what result to expect, and after how many weeks it can be judged.
6. Say plainly what words alone cannot answer, and what needs a doctor rather than a consultant.

Rules:
- Ask before assuming. An open question beats an invented fact.
- Never invent an INCI list, a concentration or a diagnosis.
- Start one active at a time, and tell me which one.
- Answer in the language I write in.`,
  },

  review: {
    copyLink: 'Copy the link',
    where: 'Where are you adding it?',
    openSettingsOf: (client: string) => `Open ${client} settings`,
    openWith: (client: string) => `Open ${client} with the prompt`,
    clients: {
      claude: {
        label: 'Claude',
        steps: [
          'Open Settings, go to Connectors.',
          'Press Add custom connector.',
          'Paste the link into the URL field, name it anything, press Add.',
        ],
      },
      chatgpt: {
        label: 'ChatGPT',
        steps: [
          'Open Settings, go to Security & login, turn on developer mode.',
          'Open the plugins page and press + at the top right.',
          'Paste the link, set authentication to No Auth, tick the confirmation, press Create.',
        ],
      },
      perplexity: {
        label: 'Perplexity',
        steps: [
          'Open Settings, go to Connectors.',
          'Press + Custom connector and choose Remote.',
          'Paste the link into MCP Server URL, authentication None, tick the confirmation, press Add.',
        ],
        note: 'A custom connector needs Perplexity Pro, Max or Enterprise.',
      },
    },
    dueNever: "The AI hasn't opened your record yet.",
    dueSince: (ago: string) => `The AI last read your record ${ago} ago.`,
    dueFresh: 'The AI read your record this week.',
    promptNote:
      'The prompt carries instructions only. No data, no key — nothing of yours lands in someone else\u2019s chat history.',
    prompt: `You are reviewing my skincare routine as a careful consultant. I keep it in an app called nitea and you reach it through the nitea connector.

Do this in order:
1. Call get_profile and get_settings, so you know who you are reading about.
2. Call get_export for the last six weeks, then get_flags and get_reactions.
3. Say what actually happened: adherence, what was skipped and why, what the check-ins say.
4. Name at most three things worth changing, each with a reason taken from the data and a way to tell in four weeks whether it worked.
5. Say plainly what the data cannot answer, and what needs a doctor rather than a consultant.

Rules:
- Never invent an INCI list, a concentration or a diagnosis.
- A product under four weeks old is still adapting; do not judge it yet.
- Anything you write into the app goes in the language get_settings reports. To change it, ask first, then call set_settings.
- Change one thing at a time, and say which.
- Answer in the language I write in.

If you have no nitea connector, read the export file I attached instead.`,
  },

  attention: {
    title: 'Attention',
    hint: (count: string) => `To decide: ${count}. Fix the cause and the line goes away.`,
    emptyTitle: 'All sorted out',
    emptyHint:
      'Nothing is waiting on you. Lines show up on their own when a date passes or the AI writes something down.',
    close: 'Close',
    sorted: 'Sorted',
    open: 'Open',
    nothingMarkedRead: 'Nothing here is marked read. A line goes away when its reason does.',
    expiredOn: (date: string, inRoutine: boolean) =>
      inRoutine ? `expired ${date}, still in the routine` : `expired ${date}`,
    expiredBadge: 'expired',
    empty: (inRoutine: boolean): string => (inRoutine ? 'empty, still in the routine' : 'empty'),
    emptyBadge: 'empty',
    expiresIn: (days: string) => `expires in ${days} — just so you know`,
    raisedOn: (date: string) => `raised ${date}`,
  },

  notes: {
    title: 'Notes from the AI',
    hint: 'Myths debunked and things worth knowing — nothing to do here',
    emptyTitle: 'Nothing written down yet',
  },

  errors: {
    atMost: (value: string) => `at most ${value}`,
    atLeast: (value: string) => `at least ${value}`,
    missingField: 'field missing',
    badFormat: 'wrong format',
  },

  calendar: {
    weekdayLetters: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    weekdaysShort: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    months: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthsGenitive: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
  },

  frequency: {
    and: ' and ',
    separator: ', ',
    daily: 'daily',
    byWeekday: 'by weekday',
    everyOtherDay: 'every other day',
    everyNDays: (days: string) => `every ${days}`,
    weekly: 'weekly',
    asNeeded: 'as needed',
  },

  cities: {
    'Europe/Lisbon': 'Lisbon',
    'Europe/London': 'London',
    'Europe/Berlin': 'Berlin',
    'Europe/Warsaw': 'Warsaw',
    'Europe/Kyiv': 'Kyiv',
    'Europe/Moscow': 'Moscow',
    'Asia/Tbilisi': 'Tbilisi',
    'Asia/Dubai': 'Dubai',
    'Asia/Karachi': 'Karachi',
    'Asia/Almaty': 'Almaty',
    'Asia/Bangkok': 'Bangkok',
    'Asia/Ho_Chi_Minh': 'Ho Chi Minh City',
    'Asia/Jakarta': 'Jakarta',
    'Asia/Singapore': 'Singapore',
    'Asia/Seoul': 'Seoul',
    'Asia/Kolkata': 'Mumbai',
    'Asia/Yerevan': 'Yerevan',
    'Asia/Baku': 'Baku',
    'Asia/Tashkent': 'Tashkent',
    'Asia/Jerusalem': 'Jerusalem',
    'Europe/Istanbul': 'Istanbul',
    'Europe/Belgrade': 'Belgrade',
    'Europe/Paris': 'Paris',
    'Europe/Madrid': 'Madrid',
    'Europe/Rome': 'Rome',
    'Europe/Amsterdam': 'Amsterdam',
    'Europe/Prague': 'Prague',
    'Europe/Vilnius': 'Vilnius',
    'Europe/Riga': 'Riga',
    'Europe/Tallinn': 'Tallinn',
    'Europe/Helsinki': 'Helsinki',
    'Europe/Stockholm': 'Stockholm',
    'Africa/Cairo': 'Cairo',
    'America/Toronto': 'Toronto',
    'America/Vancouver': 'Vancouver',
    'America/Mexico_City': 'Mexico City',
    'America/Buenos_Aires': 'Buenos Aires',
    'Asia/Shanghai': 'Shanghai',
    'Asia/Tokyo': 'Tokyo',
    'Australia/Sydney': 'Sydney',
    'America/New_York': 'New York',
    'America/Chicago': 'Chicago',
    'America/Denver': 'Denver',
    'America/Los_Angeles': 'Los Angeles',
    'America/Sao_Paulo': 'Sao Paulo',
    UTC: 'UTC',
  } as Record<string, string>,

  vocabulary: {
    checkinOption: {
      better: 'better',
      'the same': 'the same',
      worse: 'worse',
      none: 'none',
      '1-2': '1-2',
      '3-5': '3-5',
      '6+': '6+',
      'a little': 'a little',
      'a lot': 'a lot',
      noticeable: 'noticeable',
      all: 'all',
      'almost all': 'almost all',
      'about half': 'about half',
      'almost none': 'almost none',
      'clearly better': 'clearly better',
      'a little better': 'a little better',
      'no change': 'no change',
      forehead: 'forehead',
      nose: 'nose',
      cheeks: 'cheeks',
      chin: 'chin',
      nowhere: 'nowhere',
      lighter: 'lighter',
      darker: 'darker',
      'not tracking': 'not tracking',
    },
    field: {
      name: 'name',
      brand: 'brand',
      category: 'category',
      texture: 'texture',
      status: 'status',
      remaining: 'remaining',
      purpose: 'purpose',
      notes: 'notes',
      inciSource: 'INCI source',
      expiresOn: 'expires on',
      openedOn: 'opened on',
      startedOn: 'started on',
      expectedEffect: 'expected effect',
      expectedSignal: 'expected signal',
      amount: 'amount',
      slot: 'slot',
      position: 'position',
      frequency: 'frequency',
      daysOfWeek: 'days of the week',
      intervalDays: 'every so many days',
      inci: 'INCI',
      actives: 'actives',
      concerns: 'for',
      fragrance: 'fragrance',
      essentialOils: 'essential oils',
      alcoholDenat: 'denatured alcohol',
      sizeMl: 'volume, ml',
      doseMl: 'one application, ml',
      paoMonths: 'months after opening',
      expiryUncertain: 'expiry uncertain',
      evaluateAfterWeeks: 'judge after, weeks',
      skinType: 'skin type',
      conditions: 'conditions',
      intolerances: 'intolerances',
      medications: 'medication',
      city: 'city',
      climate: 'climate',
    },
    action: {
      added: 'added',
      removed: 'removed',
      updated: 'updated',
    },
    flag: {
      true: 'yes',
      false: 'no',
    },
    status: {
      active: 'active',
      paused: 'paused',
      finished: 'finished',
      candidate: 'candidate',
      rejected: 'rejected',
    },

    remaining: {
      full: 'full',
      most: 'more than half',
      half: 'half',
      low: 'running low',
      empty: 'empty',
    },

    category: {
      cleanser: 'cleanser',
      'cleansing-oil': 'cleansing oil',
      bar: 'bar',
      toner: 'toner',
      essence: 'essence',
      serum: 'serum',
      ampoule: 'ampoule',
      moisturizer: 'moisturizer',
      'gel-cream': 'gel-cream',
      cream: 'cream',
      oil: 'oil',
      sunscreen: 'sunscreen',
      'eye-care': 'eye care',
      mask: 'mask',
      exfoliant: 'exfoliant',
      'spot-treatment': 'spot treatment',
      'lip-care': 'lip care',
      mist: 'mist',
      other: 'other',
    },

    active: {
      retinoid: 'retinoids',
      acid: 'acids',
      antioxidant: 'antioxidants',
      hydration: 'hydration',
      barrier: 'barrier',
      spf: 'SPF',
      soothing: 'soothing',
      preservative: 'preservative',
    },

    confidence: {
      stated: 'stated',
      estimated: 'estimated',
      unknown: 'unknown',
    },

    evidence: {
      strong: 'strong evidence',
      moderate: 'moderate evidence',
      weak: 'weak evidence',
      none: 'no evidence',
      'marketing-claim': 'marketing claim',
    },

    slot: {
      morning: 'Morning',
      evening: 'Evening',
      weekly: 'Weekly',
      'as-needed': 'As needed',
    },

    symptom: {
      stinging: 'stinging',
      burning: 'burning',
      redness: 'redness',
      peeling: 'peeling',
      dryness: 'dryness',
      itching: 'itching',
      breakout: 'breakout',
      swelling: 'swelling',
      hives: 'hives',
      other: 'other',
    },

    severity: {
      mild: 'mild',
      moderate: 'moderate',
      severe: 'severe',
    },

    level: {
      info: 'note',
      warning: 'conflict',
      alert: 'alert',
    },

    source: {
      app: 'from the app',
      mcp: 'from the AI',
      bot: 'from the bot',
      manual: 'manual',
      import: 'from import',
    },

    skinType: {
      dry: 'dry',
      normal: 'normal',
      combination: 'combination',
      oily: 'oily',
      sensitive: 'sensitive',
    },

    condition: {
      acne: 'acne',
      rosacea: 'rosacea',
      'seborrheic-dermatitis': 'seborrheic dermatitis',
      'atopic-dermatitis': 'atopic dermatitis',
      'perioral-dermatitis': 'perioral dermatitis',
      melasma: 'melasma',
      eczema: 'eczema',
      psoriasis: 'psoriasis',
    },

    concern: {
      acne: 'acne',
      'post-acne': 'post-acne',
      pigmentation: 'pigmentation',
      redness: 'redness',
      dryness: 'dryness',
      oiliness: 'oiliness',
      texture: 'texture',
      pores: 'pores',
      wrinkles: 'wrinkles',
      sensitivity: 'sensitivity',
      dullness: 'dullness',
    },

    event: {
      purchased: 'purchased',
      opened: 'opened',
      paused: 'paused',
      resumed: 'resumed',
      finished: 'finished',
      discarded: 'discarded',
    },

    angle: {
      front: 'front',
      left: 'left',
      right: 'right',
      detail: 'detail',
    },

    checkin: {
      weekly: 'weekly',
      monthly: 'monthly',
    },
  },
}
